/**
 * Shared helper: read stem levels for canvas/JS consumers.
 *
 * As of the frame-bus refactor: stem reads come from an in-memory store
 * (frame-bus.ts) instead of getComputedStyle on :root. This eliminates
 * the ~40 synchronous style-cascade flushes per frame that were the
 * primary cause of mobile-Brave jank.
 *
 * CSS-only audio reactivity still uses --st-<key>-lvl on :root — that
 * path is unchanged. This module only serves JS-side consumers.
 *
 * Public API kept identical so existing canvas files don't need to change.
 */
import { getStem, getStems } from "./frame-bus.ts";

export type StemKey =
  | "lead-vocals"
  | "backing-vocals"
  | "drums"
  | "bass"
  | "guitar"
  | "percussion"
  | "strings"
  | "synth";

export type StemLevels = Readonly<Record<StemKey, number>>;

export const readStem = (key: StemKey): number => getStem(key);

export const readAllStems = (): StemLevels => getStems();

/** Read the per-:root level CSS var directly (legacy path — kept for any
 *  caller that explicitly wants the DOM-side smoothed value, e.g. when
 *  cooperating with a CSS keyframe rather than the audio loop). */
export const readLevel = (root: HTMLElement, key: StemKey): number => {
  const raw = getComputedStyle(root).getPropertyValue(`--st-${key}-lvl`).trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
};

/** Mix two values by a t in 0..1. */
export const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Clamp helper. */
export const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));
